import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ImageIcon,
  Loader2,
  Lock,
  Mail,
  MapPin,
  Phone,
  Plus,
  School,
  Trash2,
  User,
  Eye,
  EyeOff,
  Globe,
  FileText,
  Star,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { yogaSchoolService } from '../services/yogaSchoolService'
import { uploadFile, getMediaUrlFromKey, MediaCategory } from '../services/mediaService'
import CountrySelect from '../components/CountrySelect'

/* ------------------------------------------------------------------ */
/* Local types & helpers                                               */
/* ------------------------------------------------------------------ */

type TransportMode = 'TRAIN' | 'BUS' | 'TAXI' | 'AIR'

interface HowToReachOption {
  mode: TransportMode
  details: string
}

interface HowToReachStation {
  name: string
  options: HowToReachOption[]
}

interface OwnerForm {
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  phone: string
}

interface SchoolForm {
  name: string
  slug: string
  description: string
  shortDescription: string
  address: string
  city: string
  country: string
  location: string
  phone: string
  email: string
  website: string
  lat: string
  lng: string
  yogaAllianceCertified: boolean
  isFeatured: boolean
  businessRegNumber: string
  gstNumber: string
  yogaAllianceNumber: string
}

interface UploadedMedia {
  fileKey: string
  url: string
  uploading?: boolean
  fileName?: string
}

const initialOwner: OwnerForm = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  phone: '',
}

const initialSchool: SchoolForm = {
  name: '',
  slug: '',
  description: '',
  shortDescription: '',
  address: '',
  city: '',
  country: '',
  location: '',
  phone: '',
  email: '',
  website: '',
  lat: '',
  lng: '',
  yogaAllianceCertified: false,
  isFeatured: false,
  businessRegNumber: '',
  gstNumber: '',
  yogaAllianceNumber: '',
}

const generateSlug = (name: string): string =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/
const PHONE_REGEX = /^\+?[\d\s-()]+$/
const URL_REGEX = /^https?:\/\/.+/i

/* ------------------------------------------------------------------ */
/* Reusable UI bits                                                    */
/* ------------------------------------------------------------------ */

const inputBase =
  'w-full px-3 py-2 text-sm border rounded-lg bg-white placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors'

const inputCls = (err?: boolean) =>
  `${inputBase} ${err ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`

const labelCls = 'block text-sm font-medium text-gray-700 mb-1.5'

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-red-600">{message}</p>
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const OnboardSchool = () => {
  const navigate = useNavigate()
  const [step, setStep] = useState<1 | 2>(1)
  const [owner, setOwner] = useState<OwnerForm>(initialOwner)
  const [school, setSchool] = useState<SchoolForm>(initialSchool)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [logo, setLogo] = useState<UploadedMedia | null>(null)
  const [coverImage, setCoverImage] = useState<UploadedMedia | null>(null)
  const [gallery, setGallery] = useState<UploadedMedia[]>([])
  const [verificationDocs, setVerificationDocs] = useState<UploadedMedia[]>([])

  const [howToReach, setHowToReach] = useState<HowToReachStation[]>([
    { name: '', options: [{ mode: 'TRAIN', details: '' }] },
  ])

  const [submitting, setSubmitting] = useState(false)
  // Keep step-level submit attempts separate so step 1 actions can never
  // accidentally trigger step 2's banner.
  const [ownerSubmitAttempted, setOwnerSubmitAttempted] = useState(false)
  const [schoolSubmitAttempted, setSchoolSubmitAttempted] = useState(false)
  const slugTouched = useRef(false)
  const errorSummaryRef = useRef<HTMLDivElement | null>(null)

  const progressPercent = step === 1 ? 0 : 50

  /* ---------------- Owner form handlers ---------------- */

  const updateOwner = <K extends keyof OwnerForm>(key: K, value: OwnerForm[K]) => {
    setOwner((prev) => ({ ...prev, [key]: value }))
    // Hide step-1 banner the moment the user starts fixing fields.
    if (ownerSubmitAttempted) setOwnerSubmitAttempted(false)
    if (errors[`owner.${String(key)}`]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[`owner.${String(key)}`]
        return next
      })
    }
  }

  /* ---------------- School form handlers ---------------- */

  const updateSchool = <K extends keyof SchoolForm>(key: K, value: SchoolForm[K]) => {
    setSchool((prev) => ({ ...prev, [key]: value }))
    if (schoolSubmitAttempted) setSchoolSubmitAttempted(false)
    if (errors[`school.${String(key)}`]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[`school.${String(key)}`]
        return next
      })
    }
  }

  const handleNameChange = (value: string) => {
    setSchool((prev) => ({
      ...prev,
      name: value,
      slug: slugTouched.current ? prev.slug : generateSlug(value),
    }))
    if (schoolSubmitAttempted) setSchoolSubmitAttempted(false)
    if (errors['school.name']) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next['school.name']
        return next
      })
    }
  }

  /* ---------------- Validation ---------------- */

  const validateOwner = (): Record<string, string> => {
    const e: Record<string, string> = {}
    if (!owner.firstName.trim() || owner.firstName.trim().length < 2) {
      e['owner.firstName'] = 'First name must be at least 2 characters'
    }
    if (!owner.lastName.trim() || owner.lastName.trim().length < 2) {
      e['owner.lastName'] = 'Last name must be at least 2 characters'
    }
    if (!owner.email.trim() || !EMAIL_REGEX.test(owner.email.trim())) {
      e['owner.email'] = 'Enter a valid email address'
    }
    if (!owner.password || owner.password.length < 8) {
      e['owner.password'] = 'Password must be at least 8 characters'
    } else if (!PASSWORD_REGEX.test(owner.password)) {
      e['owner.password'] =
        'Password must include uppercase, lowercase, number, and special character (@$!%*?&)'
    }
    if (owner.password !== owner.confirmPassword) {
      e['owner.confirmPassword'] = 'Passwords do not match'
    }
    if (owner.phone && !PHONE_REGEX.test(owner.phone)) {
      e['owner.phone'] = 'Enter a valid phone number'
    }
    setErrors(e)
    return e
  }

  const validateSchool = (): Record<string, string> => {
    const e: Record<string, string> = {}
    if (!school.name.trim() || school.name.trim().length < 2) {
      e['school.name'] = 'School name must be at least 2 characters'
    }
    if (!school.slug.trim() || !/^[a-z0-9-]+$/.test(school.slug.trim())) {
      e['school.slug'] = 'Slug can only contain lowercase letters, numbers, and hyphens'
    }
    if (!school.description.trim() || school.description.trim().length < 10) {
      e['school.description'] = 'Description must be at least 10 characters'
    } else if (school.description.length > 700) {
      e['school.description'] = 'Description cannot exceed 700 characters'
    }
    if (school.shortDescription && school.shortDescription.length > 200) {
      e['school.shortDescription'] = 'Short description cannot exceed 200 characters'
    }
    if (!school.city.trim() || school.city.trim().length < 2) {
      e['school.city'] = 'City is required'
    }
    if (!school.country.trim() || school.country.trim().length < 2) {
      e['school.country'] = 'Country is required'
    }
    if (!school.location.trim() && !school.city.trim()) {
      e['school.location'] = 'Location is required'
    }
    if (school.phone && !PHONE_REGEX.test(school.phone)) {
      e['school.phone'] = 'Enter a valid phone number'
    }
    if (school.email && !EMAIL_REGEX.test(school.email)) {
      e['school.email'] = 'Enter a valid email'
    }
    if (school.website && !URL_REGEX.test(school.website)) {
      e['school.website'] = 'Enter a valid URL (https://…)'
    }
    if ((school.lat && !school.lng) || (!school.lat && school.lng)) {
      e['school.lat'] = 'Provide both latitude and longitude (or neither)'
    }
    if (school.lat && school.lng) {
      const lat = Number(school.lat)
      const lng = Number(school.lng)
      if (Number.isNaN(lat) || lat < -90 || lat > 90) {
        e['school.lat'] = 'Latitude must be between -90 and 90'
      }
      if (Number.isNaN(lng) || lng < -180 || lng > 180) {
        e['school.lng'] = 'Longitude must be between -180 and 180'
      }
    }

    // How to reach: filter empty stations; if any provided, validate fully
    const filledStations = howToReach.filter((s) => s.name.trim())
    filledStations.forEach((s, i) => {
      const validOpts = s.options.filter((o) => o.mode)
      if (validOpts.length === 0) {
        e[`school.howToReach.${i}`] = `Add at least one transport option for "${s.name}"`
      }
    })

    setErrors(e)
    return e
  }

  /* ---------------- Error helpers ---------------- */

  /** Pretty labels for the error summary banner. */
  const ERROR_LABELS: Record<string, string> = {
    'owner.firstName': 'First name',
    'owner.lastName': 'Last name',
    'owner.email': 'Email',
    'owner.password': 'Password',
    'owner.confirmPassword': 'Confirm password',
    'owner.phone': 'Phone',
    'school.name': 'School name',
    'school.slug': 'URL slug',
    'school.description': 'Description',
    'school.shortDescription': 'Short description',
    'school.address': 'Street address',
    'school.city': 'City',
    'school.country': 'Country',
    'school.location': 'Area / Locality',
    'school.phone': 'School phone',
    'school.email': 'School email',
    'school.website': 'Website',
    'school.lat': 'Latitude',
    'school.lng': 'Longitude',
  }

  const labelForErrorKey = (key: string): string => {
    if (ERROR_LABELS[key]) return ERROR_LABELS[key]
    if (key.startsWith('school.howToReach.')) return 'How to reach'
    return key
  }

  const currentStepErrorEntries = useMemo(() => {
    const prefix = step === 1 ? 'owner.' : 'school.'
    return Object.entries(errors).filter(([key]) => key.startsWith(prefix))
  }, [errors, step])

  useEffect(() => {
    if (step !== 2) return

    // Always enter step 2 in a clean validation state.
    setSchoolSubmitAttempted(false)
    setErrors((prev) => {
      const next = { ...prev }
      Object.keys(next).forEach((key) => {
        if (key.startsWith('school.')) delete next[key]
      })
      return next
    })
  }, [step])

  const scrollToErrorSummary = () => {
    // Run after React commits the new error state
    requestAnimationFrame(() => {
      errorSummaryRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    })
  }

  /* ---------------- Step navigation ---------------- */

  const handleNext = () => {
    setOwnerSubmitAttempted(true)
    const ownerErrors = validateOwner()
    const count = Object.keys(ownerErrors).length
    if (count === 0) {
      // Land on step 2 with a clean slate.
      setOwnerSubmitAttempted(false)
      setSchoolSubmitAttempted(false)
      setStep(2)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    toast.error(
      `Please fix ${count} ${count === 1 ? 'issue' : 'issues'} before continuing`
    )
    scrollToErrorSummary()
  }

  const handleBack = () => {
    // Stepping back is implicit "edit my answers" — drop any stale errors so
    // the user doesn't see step 2's error banner appear automatically when
    // they come back to step 2 after fixing things on step 1.
    setErrors({})
    setOwnerSubmitAttempted(false)
    setSchoolSubmitAttempted(false)
    setStep(1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  /* ---------------- Media upload helpers ---------------- */

  const uploadingAny =
    !!logo?.uploading ||
    !!coverImage?.uploading ||
    gallery.some((g) => g.uploading) ||
    verificationDocs.some((d) => d.uploading)

  const handleSingleUpload = async (
    file: File,
    category: MediaCategory,
    setter: (m: UploadedMedia | null) => void,
  ) => {
    setter({ fileKey: '', url: '', uploading: true, fileName: file.name })
    try {
      const result = await uploadFile(file, category)
      setter({
        fileKey: result.fileKey,
        url: result.url || getMediaUrlFromKey(result.fileKey),
        uploading: false,
        fileName: file.name,
      })
    } catch (err: any) {
      console.error('Upload error', err)
      toast.error(err?.message || 'Upload failed')
      setter(null)
    }
  }

  const handleGalleryUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    const incoming = Array.from(files)
    for (const file of incoming) {
      const placeholder: UploadedMedia = {
        fileKey: '',
        url: '',
        uploading: true,
        fileName: file.name,
      }
      setGallery((prev) => [...prev, placeholder])
      try {
        const result = await uploadFile(file, 'gallery')
        setGallery((prev) =>
          prev.map((g) =>
            g === placeholder
              ? {
                  fileKey: result.fileKey,
                  url: result.url || getMediaUrlFromKey(result.fileKey),
                  uploading: false,
                  fileName: file.name,
                }
              : g,
          ),
        )
      } catch (err: any) {
        console.error('Gallery upload failed', err)
        toast.error(err?.message || 'Failed to upload one of the gallery images')
        setGallery((prev) => prev.filter((g) => g !== placeholder))
      }
    }
  }

  const handleVerificationDocsUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    const incoming = Array.from(files)
    for (const file of incoming) {
      const placeholder: UploadedMedia = {
        fileKey: '',
        url: '',
        uploading: true,
        fileName: file.name,
      }
      setVerificationDocs((prev) => [...prev, placeholder])
      try {
        // Verification docs aren't an image gallery — pick a sensible category
        const category: MediaCategory = file.type.startsWith('image/')
          ? 'generalImage'
          : 'generalImage'
        const result = await uploadFile(file, category)
        setVerificationDocs((prev) =>
          prev.map((g) =>
            g === placeholder
              ? {
                  fileKey: result.fileKey,
                  url: result.url || getMediaUrlFromKey(result.fileKey),
                  uploading: false,
                  fileName: file.name,
                }
              : g,
          ),
        )
      } catch (err: any) {
        console.error('Doc upload failed', err)
        toast.error(err?.message || 'Failed to upload a document')
        setVerificationDocs((prev) => prev.filter((g) => g !== placeholder))
      }
    }
  }

  const removeGalleryItem = (i: number) =>
    setGallery((prev) => prev.filter((_, idx) => idx !== i))

  const removeVerificationDoc = (i: number) =>
    setVerificationDocs((prev) => prev.filter((_, idx) => idx !== i))

  /* ---------------- How To Reach helpers ---------------- */

  const addStation = () =>
    setHowToReach((prev) => [
      ...prev,
      { name: '', options: [{ mode: 'TRAIN', details: '' }] },
    ])

  const removeStation = (i: number) =>
    setHowToReach((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)))

  const updateStationName = (i: number, value: string) =>
    setHowToReach((prev) => prev.map((s, idx) => (idx === i ? { ...s, name: value } : s)))

  const addOption = (stationIdx: number) =>
    setHowToReach((prev) =>
      prev.map((s, idx) =>
        idx === stationIdx
          ? { ...s, options: [...s.options, { mode: 'TRAIN', details: '' }] }
          : s,
      ),
    )

  const removeOption = (stationIdx: number, optionIdx: number) =>
    setHowToReach((prev) =>
      prev.map((s, idx) =>
        idx === stationIdx
          ? {
              ...s,
              options:
                s.options.length === 1
                  ? s.options
                  : s.options.filter((_, oIdx) => oIdx !== optionIdx),
            }
          : s,
      ),
    )

  const updateOption = (
    stationIdx: number,
    optionIdx: number,
    patch: Partial<HowToReachOption>,
  ) =>
    setHowToReach((prev) =>
      prev.map((s, idx) =>
        idx === stationIdx
          ? {
              ...s,
              options: s.options.map((o, oIdx) =>
                oIdx === optionIdx ? { ...o, ...patch } : o,
              ),
            }
          : s,
      ),
    )

  /* ---------------- Submit ---------------- */

  const buildPayload = () => {
    const ownerPayload: any = {
      firstName: owner.firstName.trim(),
      lastName: owner.lastName.trim(),
      email: owner.email.trim().toLowerCase(),
      password: owner.password,
    }
    if (owner.phone.trim()) ownerPayload.phone = owner.phone.trim()

    const schoolPayload: any = {
      name: school.name.trim(),
      slug: school.slug.trim(),
      description: school.description.trim(),
      city: school.city.trim(),
      country: school.country.trim(),
      location: school.location.trim() || school.city.trim(),
      yogaAllianceCertified: school.yogaAllianceCertified,
      isFeatured: school.isFeatured,
    }
    if (school.shortDescription.trim()) schoolPayload.shortDescription = school.shortDescription.trim()
    if (school.address.trim()) schoolPayload.address = school.address.trim()
    if (school.phone.trim()) schoolPayload.phone = school.phone.trim()
    if (school.email.trim()) schoolPayload.email = school.email.trim().toLowerCase()
    if (school.website.trim()) schoolPayload.website = school.website.trim()
    if (school.lat && school.lng) {
      schoolPayload.coordinates = { lat: Number(school.lat), lng: Number(school.lng) }
    }

    const images: any = {}
    if (logo?.fileKey) images.logo = logo.fileKey
    if (coverImage?.fileKey) images.coverImage = coverImage.fileKey
    const galleryKeys = gallery.filter((g) => g.fileKey).map((g) => g.fileKey)
    if (galleryKeys.length > 0) images.gallery = galleryKeys
    if (Object.keys(images).length > 0) schoolPayload.images = images

    const validStations = howToReach
      .filter((s) => s.name.trim() && s.options.some((o) => o.mode))
      .map((s) => ({
        name: s.name.trim(),
        options: s.options
          .filter((o) => o.mode)
          .map((o) => ({
            mode: o.mode,
            ...(o.details.trim() ? { details: o.details.trim() } : {}),
          })),
      }))
    if (validStations.length > 0) schoolPayload.howToReach = { stations: validStations }

    const meta: Record<string, string> = {}
    if (school.businessRegNumber.trim()) meta.businessRegNumber = school.businessRegNumber.trim()
    if (school.gstNumber.trim()) meta.gstNumber = school.gstNumber.trim()
    if (school.yogaAllianceNumber.trim()) meta.yogaAllianceNumber = school.yogaAllianceNumber.trim()
    if (Object.keys(meta).length > 0) schoolPayload.verificationMeta = meta

    const docKeys = verificationDocs.filter((d) => d.fileKey).map((d) => d.fileKey)
    if (docKeys.length > 0) {
      schoolPayload.verificationDocuments = docKeys
      schoolPayload.verificationDocumentTypes = docKeys.map(() => 'additional')
    }

    return { owner: ownerPayload, school: schoolPayload }
  }

  const handleSubmit = async () => {
    const schoolErrors = validateSchool()
    const errorCount = Object.keys(schoolErrors).length
    if (errorCount > 0) {
      setSchoolSubmitAttempted(true)
      toast.error(
        `Please fix ${errorCount} ${errorCount === 1 ? 'issue' : 'issues'} before submitting`
      )
      scrollToErrorSummary()
      return
    }
    setSchoolSubmitAttempted(false)
    if (uploadingAny) {
      toast.error('Please wait for uploads to finish')
      return
    }

    setSubmitting(true)
    try {
      const payload = buildPayload()
      const res = await yogaSchoolService.onboardSchool(payload)
      if (res.success) {
        toast.success('School onboarded successfully')
        const newId =
          (res.data as any)?.school?._id || (res.data as any)?.school?.id
        if (newId) {
          navigate(`/admin/yoga-schools/${newId}`)
        } else {
          navigate('/admin/yoga-schools')
        }
      } else {
        throw new Error(res.message || 'Onboarding failed')
      }
    } catch (err: any) {
      console.error('Onboard school error', err)
      toast.error(err?.message || 'Failed to onboard school')
    } finally {
      setSubmitting(false)
    }
  }

  /* ---------------- Render ---------------- */

  const stepLabel = useMemo(() => (step === 1 ? 'Owner Details' : 'School Details'), [step])

  return (
    <div className="max-w-4xl mx-auto pb-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-6"
      >
        <h1 className="text-2xl font-bold text-gray-900">Onboard a New Yoga School</h1>
        <p className="mt-1 text-sm text-gray-500 max-w-2xl">
          Fill in the school owner&apos;s details and the school information below. The owner
          will be created as an active YOGA_SCHOOL user (no email verification needed) and the
          school will be saved as approved &amp; verified.
        </p>
      </motion.div>

      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center">
          <div className="flex flex-col items-center flex-1">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                step >= 1 ? 'bg-amber-500 text-white shadow' : 'bg-gray-200 text-gray-500'
              }`}
            >
              {step > 1 ? <CheckCircle className="h-5 w-5" /> : '1'}
            </div>
            <span
              className={`mt-1.5 text-xs font-medium ${
                step >= 1 ? 'text-amber-600' : 'text-gray-400'
              }`}
            >
              Owner
            </span>
          </div>
          <div className="flex-1 h-1.5 mx-2 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-amber-500"
              initial={{ width: '0%' }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <div className="flex flex-col items-center flex-1">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                step >= 2 ? 'bg-amber-500 text-white shadow' : 'bg-gray-200 text-gray-500'
              }`}
            >
              {step > 2 ? <CheckCircle className="h-5 w-5" /> : '2'}
            </div>
            <span
              className={`mt-1.5 text-xs font-medium ${
                step >= 2 ? 'text-amber-600' : 'text-gray-400'
              }`}
            >
              School
            </span>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-2 text-center">Step {step} of 2 · {stepLabel}</p>
      </div>

      <motion.form
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        onKeyDown={(e) => {
          // Prevent implicit form submission when the user presses Enter
          // inside a regular input. Without this, hitting Enter while typing
          // a field would fire validateSchool and pop the error banner even
          // though the user was just tabbing through fields.
          const target = e.target as HTMLElement
          if (
            e.key === 'Enter' &&
            target.tagName !== 'TEXTAREA' &&
            (target as HTMLInputElement).type !== 'submit'
          ) {
            e.preventDefault()
          }
        }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 space-y-6">
          {(step === 1 ? ownerSubmitAttempted : schoolSubmitAttempted) &&
            currentStepErrorEntries.length > 0 && (
            <div
              ref={errorSummaryRef}
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-4"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-red-800">
                    Please fix the following before continuing:
                  </h3>
                  <ul className="mt-2 space-y-1 text-sm text-red-700 list-disc list-inside">
                    {currentStepErrorEntries.map(([key, message]) => (
                      <li key={key}>
                        <span className="font-medium">{labelForErrorKey(key)}:</span> {message}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                  <User className="h-5 w-5 text-amber-600" />
                  <h2 className="text-base font-semibold text-gray-900">School Owner Account</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>
                      First name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={owner.firstName}
                      onChange={(e) => updateOwner('firstName', e.target.value)}
                      maxLength={50}
                      className={inputCls(!!errors['owner.firstName'])}
                      placeholder="e.g. Anika"
                    />
                    <FieldError message={errors['owner.firstName']} />
                  </div>
                  <div>
                    <label className={labelCls}>
                      Last name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={owner.lastName}
                      onChange={(e) => updateOwner('lastName', e.target.value)}
                      maxLength={50}
                      className={inputCls(!!errors['owner.lastName'])}
                      placeholder="e.g. Sharma"
                    />
                    <FieldError message={errors['owner.lastName']} />
                  </div>
                </div>

                <div>
                  <label className={labelCls}>
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                      type="email"
                      value={owner.email}
                      onChange={(e) => updateOwner('email', e.target.value)}
                      className={`pl-9 ${inputCls(!!errors['owner.email'])}`}
                      placeholder="owner@school.com"
                    />
                  </div>
                  <FieldError message={errors['owner.email']} />
                  <p className="mt-1 text-xs text-gray-500">
                    Owner can later sign in to the school dashboard with this email.
                  </p>
                </div>

                <div>
                  <label className={labelCls}>
                    Phone <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                      type="tel"
                      value={owner.phone}
                      onChange={(e) => updateOwner('phone', e.target.value)}
                      className={`pl-9 ${inputCls(!!errors['owner.phone'])}`}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <FieldError message={errors['owner.phone']} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={owner.password}
                        onChange={(e) => updateOwner('password', e.target.value)}
                        className={`pl-9 pr-10 ${inputCls(!!errors['owner.password'])}`}
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <FieldError message={errors['owner.password']} />
                    <p className="mt-1 text-xs text-gray-500">
                      Must include upper &amp; lower case, a number, and a special character.
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>
                      Confirm password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={owner.confirmPassword}
                        onChange={(e) => updateOwner('confirmPassword', e.target.value)}
                        className={`pl-9 pr-10 ${inputCls(!!errors['owner.confirmPassword'])}`}
                        placeholder="Repeat password"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((s) => !s)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    <FieldError message={errors['owner.confirmPassword']} />
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                  <School className="h-5 w-5 text-amber-600" />
                  <h2 className="text-base font-semibold text-gray-900">School Information</h2>
                </div>

                {/* Basic info */}
                <div>
                  <label className={labelCls}>
                    School name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={school.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    maxLength={100}
                    className={inputCls(!!errors['school.name'])}
                    placeholder="e.g. Serenity Yoga School"
                  />
                  <FieldError message={errors['school.name']} />
                </div>

                <div>
                  <label className={labelCls}>
                    URL slug <span className="text-red-500">*</span>{' '}
                    <span className="text-gray-400 text-xs font-normal">
                      (auto-generated, edit if needed)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={school.slug}
                    onChange={(e) => {
                      slugTouched.current = true
                      updateSchool('slug', e.target.value)
                    }}
                    maxLength={100}
                    className={`font-mono ${inputCls(!!errors['school.slug'])}`}
                    placeholder="serenity-yoga-school"
                  />
                  <FieldError message={errors['school.slug']} />
                </div>

                <div>
                  <label className={labelCls}>
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={4}
                    value={school.description}
                    onChange={(e) => updateSchool('description', e.target.value)}
                    maxLength={700}
                    className={`resize-none ${inputCls(!!errors['school.description'])}`}
                    placeholder="Tell students about the school, its philosophy and what makes it unique."
                  />
                  <div className="flex justify-between items-center mt-1">
                    <FieldError message={errors['school.description']} />
                    <span className="text-xs text-gray-400 ml-auto">
                      {school.description.length}/700
                    </span>
                  </div>
                </div>

                <div>
                  <label className={labelCls}>
                    Short description{' '}
                    <span className="text-gray-400 text-xs font-normal">(Optional, ≤ 200)</span>
                  </label>
                  <textarea
                    rows={2}
                    value={school.shortDescription}
                    onChange={(e) => updateSchool('shortDescription', e.target.value)}
                    maxLength={200}
                    className={`resize-none ${inputCls(!!errors['school.shortDescription'])}`}
                    placeholder="Brief tagline / summary"
                  />
                  <div className="flex justify-between items-center mt-1">
                    <FieldError message={errors['school.shortDescription']} />
                    <span className="text-xs text-gray-400 ml-auto">
                      {school.shortDescription.length}/200
                    </span>
                  </div>
                </div>

                {/* Location block */}
                <div className="pt-2 border-t border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-amber-600" /> Location
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className={labelCls}>
                        Street address{' '}
                        <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        value={school.address}
                        onChange={(e) => updateSchool('address', e.target.value)}
                        maxLength={200}
                        className={inputCls(!!errors['school.address'])}
                        placeholder="123 Main Street, Tapovan"
                      />
                      <FieldError message={errors['school.address']} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>
                          City <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={school.city}
                          onChange={(e) => updateSchool('city', e.target.value)}
                          maxLength={50}
                          className={inputCls(!!errors['school.city'])}
                          placeholder="Rishikesh"
                        />
                        <FieldError message={errors['school.city']} />
                      </div>
                      <div>
                        <label className={labelCls}>
                          Country <span className="text-red-500">*</span>
                        </label>
                        <CountrySelect
                          value={school.country}
                          onChange={(value) => updateSchool('country', value)}
                          className={inputCls(!!errors['school.country'])}
                          placeholder="e.g. India"
                          required
                          aria-label="Country"
                        />
                        <FieldError message={errors['school.country']} />
                      </div>
                    </div>

                    <div>
                      <label className={labelCls}>
                        Area / Locality{' '}
                        <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        value={school.location}
                        onChange={(e) => updateSchool('location', e.target.value)}
                        maxLength={100}
                        className={inputCls(!!errors['school.location'])}
                        placeholder="Tapovan, Near Ram Jhula"
                      />
                      <FieldError message={errors['school.location']} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>
                          Latitude{' '}
                          <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                        </label>
                        <input
                          type="number"
                          step="any"
                          value={school.lat}
                          onChange={(e) => updateSchool('lat', e.target.value)}
                          className={inputCls(!!errors['school.lat'])}
                          placeholder="30.0869"
                        />
                        <FieldError message={errors['school.lat']} />
                      </div>
                      <div>
                        <label className={labelCls}>
                          Longitude{' '}
                          <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                        </label>
                        <input
                          type="number"
                          step="any"
                          value={school.lng}
                          onChange={(e) => updateSchool('lng', e.target.value)}
                          className={inputCls(!!errors['school.lng'])}
                          placeholder="78.2676"
                        />
                        <FieldError message={errors['school.lng']} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact block */}
                <div className="pt-2 border-t border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Globe className="h-4 w-4 text-amber-600" /> Contact &amp; web
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>
                        Public phone{' '}
                        <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                      </label>
                      <input
                        type="tel"
                        value={school.phone}
                        onChange={(e) => updateSchool('phone', e.target.value)}
                        className={inputCls(!!errors['school.phone'])}
                        placeholder="+91 98765 43210"
                      />
                      <FieldError message={errors['school.phone']} />
                    </div>
                    <div>
                      <label className={labelCls}>
                        Public email{' '}
                        <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                      </label>
                      <input
                        type="email"
                        value={school.email}
                        onChange={(e) => updateSchool('email', e.target.value)}
                        className={inputCls(!!errors['school.email'])}
                        placeholder="contact@school.com"
                      />
                      <FieldError message={errors['school.email']} />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className={labelCls}>
                      Website{' '}
                      <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                    </label>
                    <input
                      type="url"
                      value={school.website}
                      onChange={(e) => updateSchool('website', e.target.value)}
                      className={inputCls(!!errors['school.website'])}
                      placeholder="https://school.com"
                    />
                    <FieldError message={errors['school.website']} />
                  </div>
                </div>

                {/* Flags */}
                <div className="pt-2 border-t border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-600" /> Visibility &amp; certification
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <label className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 flex-1">
                      <input
                        type="checkbox"
                        checked={school.yogaAllianceCertified}
                        onChange={(e) =>
                          updateSchool('yogaAllianceCertified', e.target.checked)
                        }
                        className="h-4 w-4 text-amber-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Yoga Alliance certified</span>
                    </label>
                    <label className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 flex-1">
                      <input
                        type="checkbox"
                        checked={school.isFeatured}
                        onChange={(e) => updateSchool('isFeatured', e.target.checked)}
                        className="h-4 w-4 text-amber-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Mark as featured</span>
                    </label>
                  </div>
                </div>

                {/* Media */}
                <div className="pt-2 border-t border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <ImageIcon className="h-4 w-4 text-amber-600" /> Media{' '}
                    <span className="text-xs text-gray-400 font-normal">(Optional)</span>
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Logo</label>
                      <MediaSlot
                        media={logo}
                        accept="image/*"
                        emptyLabel="Upload logo"
                        onPick={(file) => handleSingleUpload(file, 'logo', setLogo)}
                        onRemove={() => setLogo(null)}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Cover image</label>
                      <MediaSlot
                        media={coverImage}
                        accept="image/*"
                        emptyLabel="Upload cover"
                        onPick={(file) =>
                          handleSingleUpload(file, 'coverImage', setCoverImage)
                        }
                        onRemove={() => setCoverImage(null)}
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className={labelCls}>Gallery images</label>
                    <label className="block">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          handleGalleryUpload(e.target.files)
                          e.currentTarget.value = ''
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
                      />
                    </label>
                    {gallery.length > 0 && (
                      <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {gallery.map((g, i) => (
                          <ThumbCard
                            key={`${g.fileKey || g.fileName}-${i}`}
                            item={g}
                            onRemove={() => removeGalleryItem(i)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* How to reach */}
                <div className="pt-2 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-amber-600" /> How to reach{' '}
                      <span className="text-xs text-gray-400 font-normal">(Optional)</span>
                    </h3>
                    <button
                      type="button"
                      onClick={addStation}
                      className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-800"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add station
                    </button>
                  </div>
                  <div className="space-y-3">
                    {howToReach.map((station, sIdx) => (
                      <div
                        key={sIdx}
                        className="border border-gray-200 rounded-lg p-3 space-y-3 bg-gray-50/40"
                      >
                        <div className="flex gap-2 items-start">
                          <input
                            type="text"
                            value={station.name}
                            onChange={(e) => updateStationName(sIdx, e.target.value)}
                            className={inputCls(!!errors[`school.howToReach.${sIdx}`])}
                            placeholder="Station / airport name (e.g. Rishikesh Railway Station)"
                            maxLength={150}
                          />
                          {howToReach.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeStation(sIdx)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                              aria-label="Remove station"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <FieldError message={errors[`school.howToReach.${sIdx}`]} />
                        <div className="space-y-2">
                          {station.options.map((opt, oIdx) => (
                            <div key={oIdx} className="flex gap-2">
                              <select
                                value={opt.mode}
                                onChange={(e) =>
                                  updateOption(sIdx, oIdx, {
                                    mode: e.target.value as TransportMode,
                                  })
                                }
                                className={`${inputBase} border-gray-200 max-w-[110px]`}
                              >
                                <option value="TRAIN">Train</option>
                                <option value="BUS">Bus</option>
                                <option value="TAXI">Taxi</option>
                                <option value="AIR">Air</option>
                              </select>
                              <input
                                type="text"
                                value={opt.details}
                                onChange={(e) =>
                                  updateOption(sIdx, oIdx, { details: e.target.value })
                                }
                                className={`${inputBase} border-gray-200 flex-1`}
                                placeholder="Details (e.g. ~15 min taxi from station)"
                                maxLength={200}
                              />
                              {station.options.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeOption(sIdx, oIdx)}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                                  aria-label="Remove option"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addOption(sIdx)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-800"
                          >
                            <Plus className="h-3 w-3" /> Add transport option
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Verification meta + docs */}
                <div className="pt-2 border-t border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-amber-600" /> Verification details{' '}
                    <span className="text-xs text-gray-400 font-normal">(Optional)</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className={labelCls}>Business reg. no.</label>
                      <input
                        type="text"
                        value={school.businessRegNumber}
                        onChange={(e) => updateSchool('businessRegNumber', e.target.value)}
                        className={inputCls(false)}
                        placeholder="CIN / LLPIN / GSTIN…"
                        maxLength={80}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>GST number</label>
                      <input
                        type="text"
                        value={school.gstNumber}
                        onChange={(e) => updateSchool('gstNumber', e.target.value)}
                        className={inputCls(false)}
                        placeholder="27AABCU9603R1ZM"
                        maxLength={80}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Yoga Alliance no.</label>
                      <input
                        type="text"
                        value={school.yogaAllianceNumber}
                        onChange={(e) => updateSchool('yogaAllianceNumber', e.target.value)}
                        className={inputCls(false)}
                        placeholder="RYS-200-12345"
                        maxLength={80}
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className={labelCls}>Verification documents</label>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      multiple
                      onChange={(e) => {
                        handleVerificationDocsUpload(e.target.files)
                        e.currentTarget.value = ''
                      }}
                      className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
                    />
                    {verificationDocs.length > 0 && (
                      <ul className="mt-3 space-y-2">
                        {verificationDocs.map((d, i) => (
                          <li
                            key={`${d.fileKey || d.fileName}-${i}`}
                            className="flex items-center gap-2 text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
                          >
                            <FileText className="h-4 w-4 text-gray-500 shrink-0" />
                            <span className="flex-1 truncate text-gray-700">
                              {d.fileName || d.fileKey}
                            </span>
                            {d.uploading ? (
                              <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
                            ) : (
                              <button
                                type="button"
                                onClick={() => removeVerificationDoc(i)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                                aria-label="Remove document"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
          <div className="text-xs text-gray-500">
            {step === 1
              ? 'Owner will be created with role YOGA_SCHOOL in pending state (inactive, not email-verified).'
              : 'School will be saved in pending state (not active, not verified) until approval.'}
          </div>
          <div className="flex gap-2 justify-end">
            {step === 2 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={submitting}
                className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium border border-gray-200 bg-white text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
            )}
            {step === 1 ? (
              <button
                type="button"
                onClick={handleNext}
                className="inline-flex items-center gap-1 px-5 py-2 text-sm font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  void handleSubmit()
                }}
                disabled={submitting || uploadingAny}
                className="inline-flex items-center gap-1 px-5 py-2 text-sm font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" /> Onboard school
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </motion.form>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Small subcomponents                                                 */
/* ------------------------------------------------------------------ */

function MediaSlot({
  media,
  accept,
  emptyLabel,
  onPick,
  onRemove,
}: {
  media: UploadedMedia | null
  accept: string
  emptyLabel: string
  onPick: (file: File) => void
  onRemove: () => void
}) {
  if (media?.uploading) {
    return (
      <div className="h-28 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
      </div>
    )
  }
  if (media?.fileKey) {
    return (
      <div className="relative h-28 rounded-lg border border-gray-200 bg-white overflow-hidden">
        <img
          src={media.url}
          alt="preview"
          className="w-full h-full object-cover"
          onError={(e) => {
            const img = e.currentTarget as HTMLImageElement
            img.style.display = 'none'
          }}
        />
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-red-500 text-white text-xs hover:bg-red-600"
          aria-label="Remove"
        >
          ×
        </button>
      </div>
    )
  }
  return (
    <label className="h-28 rounded-lg border-2 border-dashed border-gray-300 hover:border-amber-400 hover:bg-amber-50/40 cursor-pointer flex flex-col items-center justify-center text-gray-500 transition-colors">
      <ImageIcon className="h-6 w-6 mb-1" />
      <span className="text-xs">{emptyLabel}</span>
      <input
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          e.currentTarget.value = ''
          if (f) onPick(f)
        }}
      />
    </label>
  )
}

function ThumbCard({
  item,
  onRemove,
}: {
  item: UploadedMedia
  onRemove: () => void
}) {
  return (
    <div className="relative aspect-square rounded-lg border border-gray-200 bg-white overflow-hidden">
      {item.uploading ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-50">
          <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
        </div>
      ) : (
        <>
          <img
            src={item.url}
            alt="thumb"
            className="w-full h-full object-cover"
            onError={(e) => {
              const img = e.currentTarget as HTMLImageElement
              img.style.display = 'none'
            }}
          />
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] hover:bg-red-600"
            aria-label="Remove"
          >
            ×
          </button>
        </>
      )}
    </div>
  )
}

export default OnboardSchool
